import React, { useState, useEffect } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import { PDFDocument } from "pdf-lib";
import { saveAs } from "file-saver";
import JSZip from "jszip";
import * as pdfjsLib from 'pdfjs-dist';
import { createWorker } from "tesseract.js"; // Importar Tesseract.js
import {
  Container,
  Card,
  Row,
  Col,
  Form,
  Button,
  Table,
  Alert,
  Spinner,
  Badge,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import {
  FaFilePdf,
  FaDownload,
  FaCog,
  FaFileArchive,
  FaShieldAlt,
  FaExclamationTriangle,
  FaInfo,
  FaSync,
  FaEdit,
  FaEye,
} from "react-icons/fa";
import { getFirestore, collection, getDocs } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import firebaseApp from "../../firebase/credenciales";

const db = getFirestore(firebaseApp);
const functions = getFunctions(firebaseApp);

const getArrayBufferFromFile = async (file) => {
  if (
    file.type === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
    file.type === "application/msword" ||
    file.name.toLowerCase().endsWith(".docx") ||
    file.name.toLowerCase().endsWith(".doc")
  ) {
    console.log(`Convirtiendo ${file.name} a PDF vía Cloud Function...`);
    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result.split(",")[1]);
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });

    const convertWordToPdf = httpsCallable(functions, "convertWordToPdf");
    const result = await convertWordToPdf({ base64, fileName: file.name });
    const pdfBase64 = result.data.pdfBase64;

    const binaryString = window.atob(pdfBase64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }
  return await file.arrayBuffer();
};

const meses = [
  { nombre: "Enero", valor: "01" },
  { nombre: "Febrero", valor: "02" },
  { nombre: "Marzo", valor: "03" },
  { nombre: "Abril", valor: "04" },
  { nombre: "Mayo", valor: "05" },
  { nombre: "Junio", valor: "06" },
  { nombre: "Julio", valor: "07" },
  { nombre: "Agosto", valor: "08" },
  { nombre: "Septiembre", valor: "09" },
  { nombre: "Octubre", valor: "10" },
  { nombre: "Noviembre", valor: "11" },
  { nombre: "Diciembre", valor: "12" },
];

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 7 }, (_, i) => currentYear + 1 - i);
const weeks = Array.from({ length: 65 }, (_, i) =>
  (i + 1).toString().padStart(2, "0")
);

const extractMesAnioFromPeriodo = (text) => {
  const match = text.match(
    /Periodo de Pago Del (\d{4})\/(\d{2})\/\d{2} al (\d{4})\/(\d{2})\/\d{2}/i
  );
  if (match) {
    return { anio: match[1], mes: match[2] };
  }
  return null;
};

// Función mejorada para extraer DNI excluyendo RUC
const extractDNI = (text, rucList = []) => {
  // Crear set de RUCs normalizados (sin ceros iniciales) para comparación
  const rucSet = new Set(rucList.map((ruc) => ruc.toString().replace(/^0+/, '')));

  // Patrones de búsqueda para DNI con contexto
  const dniPatterns = [
    // Prioridad 1: DNI con contexto directo estricto (ej. "DNI : 48577845" o "Documento: 48577845")
    /(?:DNI|Doc\.?|Documento|Identidad|N°\s*Doc\.?)\s*:?\s*([0-9]{8,9})\b/gi,
    // Prioridad 2: DNI flexible (permite hasta 35 caracteres intermedios si el texto se separó levemente)
    /(?:DNI|Doc\.?|Documento|Identidad)[\s\S]{0,35}?([0-9]{8,9})\b/gi,
    // Prioridad 3: DNI asociado a "la otra parte" o "EL TRABAJADOR"
    /(?:la\s+otra\s+parte|trabajador)[\s\S]{1,250}?(?:DNI|PTP|CE|Doc|Documento)[\s\/A-Za-z:º°]*(?:N[°º])?[\s:.]*([0-9]{8,9})\b/gi,
    // Prioridad 4: Formato combinado DNI/PTP/CE (común en contratos)
    /(?:DNI\/PTP\/CE)\s*(?:N[°º]?)?\s*([0-9]{8,9})\b/gi,
    // Trabajador / Empleado con DNI
    /(?:Trabajador|Empleado).*?DNI\s*:?\s*([0-9]{8,9})\b/gi,
  ];

  // Buscar DNI con contexto
  for (const pattern of dniPatterns) {
    const matches = Array.from(text.matchAll(pattern));
    for (const match of matches) {
      const dni = match[1];
      // Normalizar DNI removiendo ceros iniciales para comparación
      const dniNormalized = dni.replace(/^0+/, '');
      // Verificar que no sea un RUC normalizado
      if (!rucSet.has(dniNormalized)) {
        return dni; // Retornar DNI original con ceros si es válido
      }
    }
  }

  // Si no se encuentra con contexto, buscar números de 8 o 9 dígitos
  const allEightDigits = Array.from(text.matchAll(/\b\d{8,9}\b/g)).map(
    (m) => m[0]
  );

  // Filtrar números que no sean RUC ni fechas en formato DDMMYYYY o YYYYMMDD
  const validDNIs = allEightDigits.filter((dni) => {
    const dniNormalized = dni.replace(/^0+/, '');
    return !rucSet.has(dniNormalized);
  });

  // Si solo hay un DNI válido, devolverlo
  if (validDNIs.length === 1) {
    return validDNIs[0];
  }

  // Si hay múltiples opciones, priorizar el DNI que está más cerca de la etiqueta "DNI" o "TRABAJADOR"
  if (validDNIs.length > 1) {
    const contextRegex =
      /(?:trabajador|empleado|personal|colaborador|dni|documento)[\s\S]{0,50}?(\d{8,9})/gi;
    const matches = Array.from(text.matchAll(contextRegex));

    for (const match of matches) {
      const foundDni = match[1];
      if (validDNIs.includes(foundDni)) {
        return foundDni;
      }
    }

    // Si no se encuentra en contexto, devolver el primero válido
    return validDNIs[0];
  }

  // Si no se encuentra ningún DNI válido
  return null;
};

const RenombrarBoletas = () => {
  const [splitFiles, setSplitFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [alertaFrecuencia, setAlertaFrecuencia] = useState("");
  const [alertaMes, setAlertaMes] = useState("");
  const [alertaDNI, setAlertaDNI] = useState("");
  const [frecuencia, setFrecuencia] = useState("mensual");
  const [mes, setMes] = useState(meses[new Date().getMonth()].valor);
  const [anio, setAnio] = useState(currentYear.toString());
  const [semana, setSemana] = useState("01");
  const [modo, setModo] = useState("dividir");
  const [pdfInfoList, setPdfInfoList] = useState([]);
  const fileInputRef = React.useRef(null);
  const [paginasPorDocumento, setPaginasPorDocumento] = useState(1);
  const [rucList, setRucList] = useState([]); // Lista de RUCs para excluir

  // Cargar lista de RUCs al montar el componente
  useEffect(() => {
    const fetchRUCs = async () => {
      try {
        // Obtener RUCs desde la colección 'company' de Firestore
        const companiesRef = collection(db, "company");
        const companiesSnapshot = await getDocs(companiesRef);

        const rucs = [];
        companiesSnapshot.forEach((doc) => {
          const companyData = doc.data();
          if (companyData.ruc) {
            rucs.push(companyData.ruc);
          }
        });

        // Agregar DNI restringido manualmente
        rucs.push("45051143");

        setRucList(rucs);
        console.log("✅ RUCs cargados desde Firestore para exclusión:", rucs);
      } catch (error) {
        console.error("❌ Error al cargar RUCs desde Firestore:", error);
        // Fallback: lista vacía si hay error
        setRucList([]);
      }
    };

    fetchRUCs();
  }, []);

  useEffect(() => {
    // Configurar el worker de PDF.js usando CDN para compatibilidad
    pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;
  }, []);

  const handleReset = () => {
    setSplitFiles([]);
    setPdfInfoList([]);
    setLoading(false);
    setError("");
    setSuccess("");
    setAlertaFrecuencia("");
    setAlertaMes("");
    setAlertaDNI("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const extractTextWithOCR = async (arrayBuffer, pageNum) => {
    try {
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdf.getPage(pageNum);

      // Configurar escala para mejor OCR (más grande = mejor precisión, pero más lento)
      const scale = 2.0;
      const viewport = page.getViewport({ scale });

      // Crear canvas
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");
      canvas.height = viewport.height;
      canvas.width = viewport.width;

      // Renderizar página en canvas
      const renderContext = {
        canvasContext: context,
        viewport: viewport,
      };

      await page.render(renderContext).promise;

      // Convertir canvas a imagen
      const imageData = canvas.toDataURL("image/png");

      // Inicializar worker de Tesseract (API v6: idioma directamente en createWorker)
      const worker = await createWorker('spa');  // Idioma español pasado aquí

      // Configurar parámetros para mejor detección de texto
      await worker.setParameters({
        tessedit_char_whitelist:
          "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyzáéíóúñÁÉÍÓÚÑ-./ ", // Caracteres permitidos
        tessedit_pageseg_mode: "6", // Modo uniforme de bloque de texto
      });

      // Ejecutar OCR
      const {
        data: { text },
      } = await worker.recognize(imageData);

      // Limpiar worker
      await worker.terminate();

      console.log(
        `OCR completado para página ${pageNum}:`,
        text.substring(0, 100) + "..."
      );
      return text;
    } catch (error) {
      console.error("Error en OCR:", error);
      throw new Error("Error procesando OCR en la página");
    }
  };

  const extractTextFromPage = async (arrayBuffer, pageNum) => {
    try {
      const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
      const page = await pdf.getPage(pageNum);
      const content = await page.getTextContent();
      const sortedItems = [...content.items].sort((a, b) => {
        const ay = Array.isArray(a.transform) ? a.transform[5] : 0;
        const by = Array.isArray(b.transform) ? b.transform[5] : 0;
        if (Math.abs(by - ay) > 5) return by - ay;
        const ax = Array.isArray(a.transform) ? a.transform[4] : 0;
        const bx = Array.isArray(b.transform) ? b.transform[4] : 0;
        return ax - bx;
      });
      const normalText = sortedItems.map((item) => item.str).join(" ");

      // Verificar si la página tiene texto extraíble (no es una imagen)
      const hasText = content.items.some((item) => item.str.trim().length > 0);

      if (hasText) {
        console.log(
          `Texto normal extraído de página ${pageNum}:`,
          normalText.substring(0, 100) + "..."
        );
        return normalText;
      } else {
        // La página es una imagen (sin texto extraíble), activar OCR
        console.log(`Página ${pageNum} es una imagen, activando OCR...`);
        const ocrText = await extractTextWithOCR(arrayBuffer, pageNum);
        return ocrText;
      }
    } catch (error) {
      console.error("Error extrayendo texto:", error);
      // Fallback a OCR si falla la extracción normal
      try {
        console.log(`Fallback a OCR para página ${pageNum}...`);
        return await extractTextWithOCR(arrayBuffer, pageNum);
      } catch (ocrError) {
        console.error("OCR también falló:", ocrError);
        return ""; // Retornar vacío si todo falla
      }
    }
  };

  // Permitir varios PDFs multipágina y mostrar nombre y páginas
  const handleMultiPDFUpload = async (event) => {
    setLoading(true);
    setError("");
    setSuccess("");
    setAlertaFrecuencia("");
    setAlertaMes("");
    setAlertaDNI("");
    setSplitFiles([]);
    setPdfInfoList([]);
    try {
      const files = Array.from(event.target.files);
      const results = [];
      const infoList = [];
      let dniWarnings = [];

      for (const file of files) {
        const arrayBuffer = await getArrayBufferFromFile(file);
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        const totalPages = pdfDoc.getPageCount();

        // Guardar info para mostrar
        infoList.push({ name: file.name, pages: totalPages });

        // Analizar el texto de la primera página para sugerir frecuencia y mes
        const firstPageText = await extractTextFromPage(arrayBuffer, 1);

        // Sugerencia de frecuencia semanal si detecta la palabra "seman"
        const semanRegex = /seman/i;
        if (semanRegex.test(firstPageText) && frecuencia !== "semanal") {
          setAlertaFrecuencia(
            `⚠️ El PDF "${file.name}" parece corresponder a una boleta semanal (se detectó la palabra "semana" en el documento). Verifica la frecuencia seleccionada.`
          );
        }

        // Sugerencia de mes si el mes detectado en el texto no coincide con el seleccionado
        const mesRegex = new RegExp(
          `\\b(${meses.map((m) => m.nombre).join("|")})\\b`,
          "i"
        );
        const mesDetectado = firstPageText.match(mesRegex);
        if (
          mesDetectado &&
          meses.find(
            (m) => m.nombre.toLowerCase() === mesDetectado[0].toLowerCase()
          )?.valor !== mes
        ) {
          setAlertaMes(
            `⚠️ El mes detectado en el PDF es "${mesDetectado[0]
            }", pero tienes seleccionado "${meses.find((m) => m.valor === mes).nombre
            }". Verifica el mes seleccionado.`
          );
        }

        if (!mesDetectado) {
          const periodo = extractMesAnioFromPeriodo(firstPageText);
          if (periodo) {
            const mesNombrePeriodo =
              meses.find((m) => m.valor === periodo.mes)?.nombre || periodo.mes;
            const mesNombreSeleccionado =
              meses.find((m) => m.valor === mes)?.nombre || mes;
            setAlertaMes(
              `⚠️ El documento indica que pertenece a ${mesNombrePeriodo} ${periodo.anio}. Mes seleccionado: ${mesNombreSeleccionado} ${anio}.`
            );
          } else {
            setAlertaMes(
              "⚠️ No se pudo detectar el mes en el PDF. Verifica que el documento sea correcto."
            );
          }
        }

        // DIVISIÓN EN BLOQUES DE N PÁGINAS
        for (let i = 0; i < totalPages; i += paginasPorDocumento) {
          // Extraer el texto de la primera página del bloque para el DNI
          const pageText = await extractTextFromPage(arrayBuffer, i + 1);

          // Usar la función mejorada de extracción de DNI
          let dni = extractDNI(pageText, rucList);

          if (!dni) {
            dniWarnings.push(
              `⚠️ No se pudo detectar un DNI válido en el bloque que inicia en la página ${i + 1
              } del archivo "${file.name
              }". Los RUCs de empresas han sido excluidos automáticamente.`
            );
            dni = `dni_${i + 1}_${Date.now()}`;
          }

          let newName = "";
          if (frecuencia === "mensual") {
            newName = `${dni}-${mes}_${anio}.pdf`;
          } else if (frecuencia === "semanal") {
            newName = `${dni}-${mes}_${anio}_SEM${semana}.pdf`;
          }

          // Asegurar nombre único
          let baseName = newName;
          let counter = 1;
          while (results.some((f) => f.newName === newName)) {
            newName = `${baseName.replace(/\.pdf$/, "")}(${counter}).pdf`;
            counter++;
          }

          const newPdf = await PDFDocument.create();
          // Copiar N páginas
          const endPage = Math.min(i + paginasPorDocumento, totalPages);
          const copiedPages = await newPdf.copyPages(
            pdfDoc,
            Array.from({ length: endPage - i }, (_, idx) => i + idx)
          );
          copiedPages.forEach((page) => newPdf.addPage(page));
          const pdfBytes = await newPdf.save();
          const blob = new Blob([pdfBytes], { type: "application/pdf" });

          results.push({
            blob,
            newName,
          });
        }
      }

      // Mostrar advertencias de DNI si las hay
      if (dniWarnings.length > 0) {
        setAlertaDNI(dniWarnings.join("\n"));
      }

      setPdfInfoList(infoList);
      setSplitFiles(results);
      setSuccess(
        `PDF(s) dividido(s) correctamente. Se generaron ${results.length
        } documentos. ${rucList.length > 0
          ? `Se excluyeron ${rucList.length} RUCs de empresas.`
          : ""
        } Puedes descargar los archivos individuales o el ZIP.`
      );
    } catch (err) {
      console.error("Error procesando PDFs:", err);
      setError("Ocurrió un error procesando los PDF.");
    }
    setLoading(false);
  };

  // Modo renombrar PDFs individuales
  const handleRenameFiles = async (event) => {
    setLoading(true);
    setError("");
    setSuccess("");
    setAlertaFrecuencia("");
    setAlertaMes("");
    setAlertaDNI("");
    setSplitFiles([]);
    setPdfInfoList([]);
    try {
      const files = Array.from(event.target.files);
      const results = [];
      const infoList = [];
      let dniWarnings = [];

      for (let file of files) {
        const arrayBuffer = await getArrayBufferFromFile(file);
        const pdfDoc = await PDFDocument.load(arrayBuffer);
        const totalPages = pdfDoc.getPageCount();

        infoList.push({ name: file.name, pages: totalPages });

        const firstPageText = await extractTextFromPage(arrayBuffer, 1);

        const semanRegex = /seman/i;
        if (semanRegex.test(firstPageText) && frecuencia !== "semanal") {
          setAlertaFrecuencia(
            '⚠️ El PDF parece corresponder a una boleta semanal (se detectó la palabra "semana" en el documento). Verifica la frecuencia seleccionada.'
          );
        }

        const mesRegex = new RegExp(
          `\\b(${meses.map((m) => m.nombre).join("|")})\\b`,
          "i"
        );
        const mesDetectado = firstPageText.match(mesRegex);
        if (
          mesDetectado &&
          meses.find(
            (m) => m.nombre.toLowerCase() === mesDetectado[0].toLowerCase()
          )?.valor !== mes
        ) {
          setAlertaMes(
            `⚠️ El mes detectado en el PDF es "${mesDetectado[0]
            }", pero tienes seleccionado "${meses.find((m) => m.valor === mes).nombre
            }". Verifica el mes seleccionado.`
          );
        }

        if (!mesDetectado) {
          const periodo = extractMesAnioFromPeriodo(firstPageText);
          if (periodo) {
            const mesNombrePeriodo =
              meses.find((m) => m.valor === periodo.mes)?.nombre || periodo.mes;
            const mesNombreSeleccionado =
              meses.find((m) => m.valor === mes)?.nombre || mes;
            setAlertaMes(
              `⚠️ El documento indica que pertenece a ${mesNombrePeriodo} ${periodo.anio}. Mes seleccionado: ${mesNombreSeleccionado} ${anio}.`
            );
          } else {
            setAlertaMes(
              "⚠️ No se pudo detectar el mes en el PDF. Verifica que el documento sea correcto."
            );
          }
        }

        // Usar la función mejorada de extracción de DNI
        let dni = extractDNI(firstPageText, rucList);

        if (!dni) {
          dniWarnings.push(
            `⚠️ No se pudo detectar un DNI válido en el archivo "${file.name}". Los RUCs de empresas han sido excluidos automáticamente.`
          );
          dni = `dni_${Date.now()}`;
        }

        let newName = "";
        if (frecuencia === "mensual") {
          newName = `${dni}-${mes}_${anio}.pdf`;
        } else if (frecuencia === "semanal") {
          newName = `${dni}-${mes}_${anio}_SEM${semana}.pdf`;
        }

        // Asegurar nombre único
        let baseName = newName;
        let counter = 1;
        while (results.some((f) => f.newName === newName)) {
          newName = `${baseName.replace(/\.pdf$/, "")}(${counter}).pdf`;
          counter++;
        }

        results.push({
          blob: new Blob([arrayBuffer], { type: "application/pdf" }),
          newName,
        });
      }

      // Mostrar advertencias de DNI si las hay
      if (dniWarnings.length > 0) {
        setAlertaDNI(dniWarnings.join("\n"));
      }

      setPdfInfoList(infoList);
      setSplitFiles(results);
      setSuccess(
        `PDF renombrado correctamente. Se generaron ${results.length
        } documentos. ${rucList.length > 0
          ? `Se excluyeron ${rucList.length} RUCs de empresas.`
          : ""
        } Puedes descargar los archivos individuales o el ZIP.`
      );
    } catch (err) {
      console.error("Error procesando archivos:", err);
      setError("Ocurrió un error procesando los archivos.");
    }
    setLoading(false);
  };

  const handleDownload = (fileData) => {
    saveAs(fileData.blob, fileData.newName);
    setSuccess(`Archivo "${fileData.newName}" descargado correctamente.`);
  };

  const handleDownloadZip = async () => {
    const zip = new JSZip();
    splitFiles.forEach((file) => {
      zip.file(file.newName, file.blob);
    });
    let zipName = "";
    if (frecuencia === "mensual") {
      zipName = `${mes}_${anio}.zip`;
    } else if (frecuencia === "semanal") {
      zipName = `${mes}_${anio}_SEM${semana}.zip`;
    } else {
      zipName = "boletas.zip";
    }
    const content = await zip.generateAsync({ type: "blob" });
    saveAs(content, zipName);
    setSuccess(`ZIP "${zipName}" descargado correctamente.`);
  };

  return (
    <div
      className="min-vh-100"
      style={{
        background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
        minHeight: "100vh",
        width: "100vw",
        maxWidth: "100%",
        padding: "20px 0",
      }}
    >
      <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
        {/* Marco principal */}
        <Card
          className="shadow-lg border-0"
          style={{
            borderRadius: "15px",
            overflow: "hidden",
            minHeight: "90vh",
          }}
        >
          {/* Header Premium con degradado verde oscuro */}
          <Card.Header
            className="text-white py-4 position-relative"
            style={{
              background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
              borderBottom: "3px solid #f39c12",
            }}
          >
            <div className="position-absolute end-0 top-50 translate-middle-y me-4" style={{ zIndex: 10 }}>
              <BotonManualHeader />
            </div>
            <div className="text-center">
              <h2 className="fw-bold mb-1">
                <FaFilePdf className="me-3" />
                Dividir y Renombrar PDF de Boletas
              </h2>
              <p className="mb-0 opacity-75">
                Procesa y organiza documentos PDF de manera automática e inteligente
              </p>
            </div>
          </Card.Header>

          <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
            {/* Indicador de RUCs cargados */}
            {rucList.length > 0 && (
              <Alert
                variant="info"
                className="mb-4"
                style={{ borderRadius: "15px" }}
              >
                <div className="d-flex align-items-center">
                  <FaShieldAlt
                    size={24}
                    className="me-3"
                    style={{ color: "#0dcaf0" }}
                  />
                  <div>
                    <strong>Validación Activa:</strong> Se han cargado{" "}
                    {rucList.length} RUCs de empresas que serán excluidos
                    automáticamente de la detección de DNI.
                  </div>
                </div>
              </Alert>
            )}

            {/* Barra superior con controles */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Body className="p-4">
                <Row className="align-items-center">
                  <Col md={1}>
                    <div
                      className="rounded-3 p-3 text-center"
                      style={{
                        background:
                          "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                        color: "white",
                      }}
                    >
                      <FaCog size={24} />
                    </div>
                  </Col>
                  <Col md={9}>
                    <h5 className="mb-1 fw-semibold">
                      Configuración de Procesamiento
                    </h5>
                    <p className="text-muted mb-0 small">
                      Ajusta los parámetros para el procesamiento automático de
                      PDFs
                    </p>
                  </Col>
                  <Col md={2} className="text-end">
                    <OverlayTrigger
                      placement="top"
                      overlay={<Tooltip>Reiniciar configuración</Tooltip>}
                    >
                      <Button
                        variant="outline-secondary"
                        onClick={handleReset}
                        disabled={loading}
                        className="d-flex align-items-center gap-2"
                        style={{ borderRadius: "10px" }}
                      >
                        <FaSync size="16px" />
                        Reiniciar
                      </Button>
                    </OverlayTrigger>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Formulario de configuración */}
            <Card
              className="shadow-sm border-0 mb-4"
              style={{ borderRadius: "15px" }}
            >
              <Card.Header
                className="text-white py-3"
                style={{
                  background:
                    "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                  borderRadius: "15px 15px 0 0",
                }}
              >
                <div className="d-flex align-items-center">
                  <FaCog className="me-2" />
                  <div>
                    <h5 className="mb-0 fw-semibold">
                      Parámetros de Procesamiento
                    </h5>
                    <small className="opacity-75">
                      Configure el modo y criterios para procesar los PDFs
                    </small>
                  </div>
                </div>
              </Card.Header>
              <Card.Body className="p-4">
                <Form>
                  <Row className="g-3 mb-4">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          Modo de Procesamiento
                        </Form.Label>
                        <Form.Select
                          value={modo}
                          onChange={(e) => setModo(e.target.value)}
                          className="form-control-lg"
                          style={{ borderRadius: "10px" }}
                        >
                          <option value="dividir">
                            Dividir y renombrar PDF(s) multipágina
                          </option>
                          <option value="renombrar">
                            Renombrar PDFs individuales
                          </option>
                        </Form.Select>
                        <small className="text-muted">
                          Selecciona el tipo de procesamiento requerido
                        </small>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          Páginas por Documento
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min={1}
                          max={50}
                          value={paginasPorDocumento}
                          onChange={(e) =>
                            setPaginasPorDocumento(Number(e.target.value))
                          }
                          disabled={loading || modo === "renombrar"}
                          className="form-control-lg"
                          style={{ borderRadius: "10px" }}
                        />
                        <small className="text-muted">
                          Ej: contratos de 3 páginas = 3
                        </small>
                      </Form.Group>
                    </Col>
                    <Col md={3}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Frecuencia</Form.Label>
                        <Form.Select
                          value={frecuencia}
                          onChange={(e) => setFrecuencia(e.target.value)}
                          className="form-control-lg"
                          style={{ borderRadius: "10px" }}
                        >
                          <option value="mensual">Mensual</option>
                          <option value="semanal">Semanal</option>
                        </Form.Select>
                        <small className="text-muted">
                          Tipo de documento a procesar
                        </small>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3 mb-4">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Mes</Form.Label>
                        <Form.Select
                          value={mes}
                          onChange={(e) => setMes(e.target.value)}
                          className="form-control-lg"
                          style={{ borderRadius: "10px" }}
                        >
                          {meses.map((m) => (
                            <option key={m.valor} value={m.valor}>
                              {m.nombre}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label className="fw-bold">Año</Form.Label>
                        <Form.Select
                          value={anio}
                          onChange={(e) => setAnio(e.target.value)}
                          className="form-control-lg"
                          style={{ borderRadius: "10px" }}
                        >
                          {years.map((y) => (
                            <option key={y} value={y}>
                              {y}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    {frecuencia === "semanal" && (
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label className="fw-bold">Semana</Form.Label>
                          <Form.Select
                            value={semana}
                            onChange={(e) => setSemana(e.target.value)}
                            className="form-control-lg"
                            style={{ borderRadius: "10px" }}
                          >
                            {weeks.map((w) => (
                              <option key={w} value={w}>
                                Semana {w}
                              </option>
                            ))}
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    )}
                  </Row>

                  <hr className="my-4" />

                  <Row>
                    <Col>
                      <Form.Group>
                        <Form.Label className="fw-bold">
                          <FaFilePdf className="me-2" />
                          {modo === "dividir"
                            ? "Selecciona uno o varios archivos PDF multipágina"
                            : "Selecciona uno o varios PDFs individuales"}
                        </Form.Label>
                        <Form.Control
                          type="file"
                          accept=".pdf,.doc,.docx"
                          multiple={true}
                          onChange={
                            modo === "dividir"
                              ? handleMultiPDFUpload
                              : handleRenameFiles
                          }
                          disabled={loading}
                          ref={fileInputRef}
                          className="form-control-lg"
                          style={{ borderRadius: "10px" }}
                        />
                        <small className="text-muted">
                          Arrastra y suelta archivos PDF o haz clic para
                          seleccionarlos
                        </small>
                      </Form.Group>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>

            {/* Estado de carga */}
            {loading && (
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Body className="p-4">
                  <div className="text-center">
                    <div
                      className="rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center"
                      style={{
                        width: "60px",
                        height: "60px",
                        background:
                          "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                        color: "white",
                      }}
                    >
                      <Spinner animation="border" variant="light" />
                    </div>
                    <h5 className="mb-2">Procesando PDF...</h5>
                    <p className="text-muted mb-3">
                      Por favor espera mientras procesamos los documentos
                    </p>
                    <ProgressBar
                      animated
                      now={100}
                      variant="success"
                      style={{ height: "8px", borderRadius: "8px" }}
                    />
                  </div>
                </Card.Body>
              </Card>
            )}

            {/* Alertas */}
            {alertaFrecuencia && (
              <Alert
                variant="warning"
                className="mb-4"
                onClose={() => setAlertaFrecuencia("")}
                dismissible
                style={{ borderRadius: "15px" }}
              >
                <div className="d-flex align-items-center">
                  <FaExclamationTriangle className="me-2" />
                  {alertaFrecuencia}
                </div>
              </Alert>
            )}

            {alertaMes && (
              <Alert
                variant="warning"
                className="mb-4"
                onClose={() => setAlertaMes("")}
                dismissible
                style={{ borderRadius: "15px" }}
              >
                <div className="d-flex align-items-center">
                  <FaExclamationTriangle className="me-2" />
                  {alertaMes}
                </div>
              </Alert>
            )}

            {alertaDNI && (
              <Alert
                variant="warning"
                className="mb-4"
                onClose={() => setAlertaDNI("")}
                dismissible
                style={{ whiteSpace: "pre-line", borderRadius: "15px" }}
              >
                <div className="d-flex align-items-start">
                  <FaExclamationTriangle className="me-2 mt-1" />
                  <div>{alertaDNI}</div>
                </div>
              </Alert>
            )}

            {error && (
              <Alert
                variant="danger"
                className="mb-4"
                onClose={() => setError("")}
                dismissible
                style={{ borderRadius: "15px" }}
              >
                <div className="d-flex align-items-center">
                  <FaExclamationTriangle className="me-2" />
                  {error}
                </div>
              </Alert>
            )}

            {success && (
              <Alert
                variant="success"
                className="mb-4"
                onClose={() => setSuccess("")}
                dismissible
                style={{ borderRadius: "15px" }}
              >
                <div className="d-flex align-items-center">
                  <FaInfo className="me-2" />
                  <span dangerouslySetInnerHTML={{ __html: success }} />
                </div>
              </Alert>
            )}

            {/* Detalle de PDFs cargados */}
            {pdfInfoList.length > 0 && (
              <Card
                className="shadow-sm border-0 mb-4"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header
                  className="text-white py-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                    borderRadius: "15px 15px 0 0",
                  }}
                >
                  <div className="d-flex align-items-center">
                    <FaEye className="me-2" />
                    <div className="flex-grow-1">
                      <h5 className="mb-0 fw-semibold">
                        Archivos PDF Cargados
                      </h5>
                      <small className="opacity-75">
                        Detalles de los documentos procesados
                      </small>
                    </div>
                    <Badge
                      bg="light"
                      text="dark"
                      style={{ fontSize: "0.9rem" }}
                    >
                      {pdfInfoList.length} archivos
                    </Badge>
                  </div>
                </Card.Header>
                <Card.Body className="p-0">
                  <Table
                    hover
                    responsive
                    className="mb-0"
                    style={{ fontSize: "0.9rem" }}
                  >
                    <thead
                      style={{
                        backgroundColor: "#f8f9fa",
                        position: "sticky",
                        top: 0,
                        zIndex: 10,
                      }}
                    >
                      <tr>
                        <th
                          className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                          style={{ width: "10%" }}
                        >
                          N°
                        </th>
                        <th
                          className="border-0 py-3 px-3 text-secondary fw-semibold"
                          style={{ width: "70%" }}
                        >
                          Nombre del Archivo
                        </th>
                        <th
                          className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                          style={{ width: "20%" }}
                        >
                          Páginas
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pdfInfoList.map((info, idx) => (
                        <tr
                          key={idx}
                          style={{ borderBottom: "1px solid #f1f3f4" }}
                        >
                          <td className="text-center py-3 px-3">
                            <Badge bg="dark" className="px-2 py-1">
                              {idx + 1}
                            </Badge>
                          </td>
                          <td className="py-3 px-3">
                            <div className="d-flex align-items-center">
                              <div
                                className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                                style={{
                                  width: "35px",
                                  height: "35px",
                                  background:
                                    "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                                  color: "white",
                                }}
                              >
                                <FaFilePdf size="14px" />
                              </div>
                              <div>
                                <div className="fw-medium text-dark small">
                                  {info.name.length > 50
                                    ? info.name.substr(0, 50) + "..."
                                    : info.name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="text-center py-3 px-3">
                            <Badge bg="info" className="px-2 py-1">
                              {info.pages} páginas
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </Card.Body>
              </Card>
            )}

            {/* Resultados de archivos procesados */}
            {splitFiles.length > 0 && (
              <Card
                className="shadow-sm border-0"
                style={{ borderRadius: "15px" }}
              >
                <Card.Header
                  className="text-white py-3"
                  style={{
                    background:
                      "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                    borderRadius: "15px 15px 0 0",
                  }}
                >
                  <Row className="align-items-center">
                    <Col>
                      <div className="d-flex align-items-center">
                        <FaFileArchive className="me-2" />
                        <div>
                          <h5 className="mb-0 fw-semibold">
                            Archivos Procesados
                          </h5>
                          <small className="opacity-75">
                            Documentos listos para descarga
                          </small>
                        </div>
                      </div>
                    </Col>
                    <Col xs="auto">
                      <OverlayTrigger
                        placement="top"
                        overlay={
                          <Tooltip>
                            Descargar todos los archivos en un ZIP
                          </Tooltip>
                        }
                      >
                        <Button
                          variant="light"
                          className="fw-bold d-flex align-items-center gap-2"
                          onClick={handleDownloadZip}
                          style={{ borderRadius: "10px" }}
                        >
                          <FaFileArchive />
                          Descargar ZIP
                        </Button>
                      </OverlayTrigger>
                    </Col>
                  </Row>
                </Card.Header>
                <Card.Body className="p-0">
                  <div style={{ maxHeight: "400px", overflowY: "auto" }}>
                    <Table
                      hover
                      responsive
                      className="mb-0"
                      style={{ fontSize: "0.9rem" }}
                    >
                      <thead
                        style={{
                          backgroundColor: "#f8f9fa",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                        }}
                      >
                        <tr>
                          <th
                            className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                            style={{ width: "8%" }}
                          >
                            N°
                          </th>
                          <th
                            className="border-0 py-3 px-3 text-secondary fw-semibold"
                            style={{ width: "72%" }}
                          >
                            Nombre del Archivo
                          </th>
                          <th
                            className="border-0 py-3 px-3 text-secondary fw-semibold text-center"
                            style={{ width: "20%" }}
                          >
                            Acciones
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {splitFiles.map((file, idx) => (
                          <tr
                            key={idx}
                            style={{ borderBottom: "1px solid #f1f3f4" }}
                          >
                            <td className="text-center py-3 px-3">
                              <Badge bg="dark" className="px-2 py-1">
                                {idx + 1}
                              </Badge>
                            </td>
                            <td className="py-3 px-3">
                              <div className="d-flex align-items-center">
                                <div
                                  className="rounded-circle me-3 d-flex align-items-center justify-content-center"
                                  style={{
                                    width: "35px",
                                    height: "35px",
                                    background:
                                      "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
                                    color: "white",
                                  }}
                                >
                                  <FaFilePdf size="14px" />
                                </div>
                                <div className="flex-grow-1">
                                  <Form.Control
                                    type="text"
                                    value={file.newName}
                                    onChange={(e) => {
                                      const updatedName = e.target.value;
                                      setSplitFiles((prev) =>
                                        prev.map((f, i) =>
                                          i === idx
                                            ? { ...f, newName: updatedName }
                                            : f
                                        )
                                      );
                                    }}
                                    className="form-control-sm"
                                    style={{ borderRadius: "8px" }}
                                  />
                                </div>
                              </div>
                            </td>
                            <td className="text-center py-3 px-3">
                              <OverlayTrigger
                                placement="top"
                                overlay={
                                  <Tooltip>
                                    Descargar archivo individual
                                  </Tooltip>
                                }
                              >
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={() => handleDownload(file)}
                                  className="d-flex align-items-center gap-1"
                                  style={{ borderRadius: "8px" }}
                                >
                                  <FaDownload size="14px" />
                                  Descargar
                                </Button>
                              </OverlayTrigger>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            )}
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

export default RenombrarBoletas;
